import { useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { PERIOD_KEYS, summarise, trendOf } from './series'
import { MIN_CARD_HEIGHT } from '../cardHeight'

const SPARK_HEIGHT = 34
const BAR_HEIGHT = 10

const pct = value => (value === null || value === undefined ? '—' : `${value.toFixed(1)}%`)

const defaultToneFor = (rate, { goodAbove, warnAbove }) => {
  if (rate === null || rate === undefined) return 'primary'
  if (rate >= goodAbove) return 'success'
  if (rate >= warnAbove) return 'warning'
  return 'error'
}

const CompositionBar = ({ sums, parts, total }) => {
  if (!total || !parts.length) return null

  const segments = parts
    .map(part => ({ ...part, value: sums[part.field] ?? 0 }))
    .filter(segment => segment.value > 0)

  return (
    <Box sx={{ display: 'flex', height: BAR_HEIGHT, borderRadius: 5, overflow: 'hidden', bgcolor: 'action.hover' }}>
      {segments.map(segment => (
        <Box key={segment.field} sx={{ width: `${(segment.value / total) * 100}%`, bgcolor: `${segment.tone}.main` }} />
      ))}
    </Box>
  )
}

const Sparkline = ({ points, toneFor }) => {
  const { t } = useTranslation()
  if (points.length < 2) return null

  const rates = points.map(point => point.rate)
  const min = Math.min(...rates)
  const span = Math.max(...rates) - min || 1

  return (
    <Box>
      <Stack direction="row" alignItems="flex-end" spacing={0.375} sx={{ height: SPARK_HEIGHT }}>
        {points.map(point => (
          <Tooltip
            key={point.date}
            title={`${dayjs(point.date).format('MMM D')} · ${pct(point.rate)}`}
            placement="top"
            arrow
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 3,
                height: `${20 + ((point.rate - min) / span) * 80}%`,
                borderRadius: 0.5,
                bgcolor: theme => alpha(theme.palette[toneFor(point.rate)].main, 0.55),
                '&:hover': { bgcolor: theme => theme.palette[toneFor(point.rate)].main },
              }}
            />
          </Tooltip>
        ))}
      </Stack>
      <Typography variant="caption" color="text.disabled">
        {t('last {{count}} days', { count: points.length })}
      </Typography>
    </Box>
  )
}

const PeriodControl = ({ variant, value, onChange, labels }) => {
  const [anchor, setAnchor] = useState(null)

  if (variant === 'menu') {
    return (
      <>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {labels[value]?.long}
          </Typography>
          <IconButton size="small" onClick={event => setAnchor(event.currentTarget)}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
          {PERIOD_KEYS.map(key => (
            <MenuItem
              key={key}
              selected={key === value}
              onClick={() => {
                onChange(key)
                setAnchor(null)
              }}
            >
              {labels[key]?.long}
            </MenuItem>
          ))}
        </Menu>
      </>
    )
  }

  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, next) => next && onChange(next)}
      sx={{
        '& .MuiToggleButton-root': {
          px: 0.875,
          py: 0.25,
          border: 'none',
          fontSize: 11,
          lineHeight: 1.6,
          textTransform: 'none',
          color: 'text.secondary',
          '&.Mui-selected': { color: 'primary.main', bgcolor: 'action.selected', fontWeight: 700 },
        },
      }}
    >
      {PERIOD_KEYS.map(key => (
        <ToggleButton key={key} value={key}>
          {labels[key]?.short}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

export default function TrendBreakdown({
  title,
  rows = [],
  dateField = 'date',
  valueField = 'value',
  totalField = 'total',
  parts = [],
  periodLabels,
  defaultPeriod = 'today',
  control = 'segmented',
  trendDays = 20,
  goodAbove = 95,
  warnAbove = 90,
  isReady = true,
  emptyMessage,
  minHeight = MIN_CARD_HEIGHT,
}) {
  const { t } = useTranslation()
  const [period, setPeriod] = useState(defaultPeriod)

  const summary = useMemo(
    () => summarise(rows, { dateField, valueField, totalField }),
    [rows, dateField, valueField, totalField],
  )

  const trend = useMemo(
    () => trendOf(summary?.days, { dateOf: summary?.dateOf, valueField, totalField, limit: trendDays }),
    [summary, valueField, totalField, trendDays],
  )

  const toneFor = rate => defaultToneFor(rate, { goodAbove, warnAbove })

  const labels = {
    today: { short: t('Today'), long: periodLabels?.today ?? t('Today') },
    week: { short: t('Week'), long: periodLabels?.week ?? t('This Week') },
    month: { short: t('Month'), long: periodLabels?.month ?? t('This Month') },
    all: { short: t('Year'), long: periodLabels?.all ?? t('This Year') },
  }

  const current = summary?.periods[period] ?? summary?.periods.today
  const delta = !summary || period === 'all' || current?.rate === null ? null : current.rate - summary.baseline

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        minHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          {title}
        </Typography>
        {isReady && summary && (
          <PeriodControl variant={control} value={period} onChange={setPeriod} labels={labels} />
        )}
      </Stack>

      {!isReady ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !summary ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {emptyMessage ?? t('No data to report.')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.75} sx={{ flex: 1, minHeight: 0 }}>
          <Box>
            <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap">
              <Typography variant="h3" fontWeight={700} color={`${toneFor(current.rate)}.main`} sx={{ lineHeight: 1 }}>
                {pct(current.rate)}
              </Typography>
              {delta !== null && Math.abs(delta) >= 0.05 && (
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  {delta < 0 ? (
                    <ArrowDropDownIcon fontSize="small" color="error" />
                  ) : (
                    <ArrowDropUpIcon fontSize="small" color="success" />
                  )}
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={delta < 0 ? 'error.main' : 'success.main'}
                  >
                    {t('{{points}} pts vs average', { points: Math.abs(delta).toFixed(1) })}
                  </Typography>
                </Stack>
              )}
            </Stack>
            {!summary.isCurrent && period === 'today' && (
              <Typography variant="caption" color="text.disabled">
                {t('as of {{date}}', { date: dayjs(summary.asOf).format('MMM D') })}
              </Typography>
            )}
          </Box>

          {parts.length > 0 && (
            <Box>
              <CompositionBar sums={current.sums} parts={parts} total={current.total} />
              <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }} flexWrap="wrap">
                {parts.map(part => (
                  <Stack key={part.field} direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: `${part.tone}.main` }} />
                    <Typography variant="caption" color="text.secondary">
                      <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {(current.sums[part.field] ?? 0).toLocaleString()}
                      </Box>{' '}
                      {t(part.label)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          <Box sx={{ mt: 'auto' }}>
            <Sparkline points={trend} toneFor={toneFor} />
          </Box>
        </Stack>
      )}
    </Paper>
  )
}
